import * as React from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import LegacyNotificationManagementService from '../../shared/services/LegacyNotificationManagementService';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  TextField,
  Paper,
  Theme,
  WithStyles,
  withStyles,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import HttpCallConfig from '../../core/models/HttpCallConfig';
import { INotificationRouteParams } from '../pages/LegacyOrganizationNotificationDetail';
import AreYouSureModal from 'src/modules/shared/dataSharing/AreYouSureModal';
import {
  IsValidEmail,
  IsValidUrl,
  IsValidFTPAddress,
  IsValidSFTPAddress,
} from 'src/modules/core/helpers/ValidationHelper';
import LegacyNotificationChannel from '../models/LegacyNotificationChannel';
import LegacyNotificationChannelTypeId from '../models/LegacyNotificationChannelTypeId';

const styles = (theme: Theme) => ({
  paper: {
    padding: theme.spacing.unit * 3,
  },
  button: {
    marginTop: theme.spacing.unit * 1,
  },
});

interface IValidation {
  'notification.channel.address'?: string;
  'notification.channel.username'?: string;
  'notification.channel.password'?: string;
}

interface IState {
  notification: LegacyNotificationChannel;
  saving: boolean;
  deleteNotification: boolean;
  errors: IValidation;
  showPassword: boolean;
}

interface IProps extends WithStyles<typeof styles>, RouteComponentProps<INotificationRouteParams> {}

class LegacyNotificationDetail extends React.Component<IProps> {
  public static AvailableChannelTypes = [
    {
      id: LegacyNotificationChannelTypeId.Email,
      name: 'Email',
      validator: IsValidEmail,
      validationMessage: 'Enter a valid email',
    },
    {
      id: LegacyNotificationChannelTypeId.Webhook,
      name: 'Webhook',
      validator: IsValidUrl,
      validationMessage: 'Enter a valid URL',
    },
    {
      id: LegacyNotificationChannelTypeId.FTP,
      name: 'FTP',
      validator: IsValidFTPAddress,
      validationMessage: 'Enter a valid FTP address',
    },
    {
      id: LegacyNotificationChannelTypeId.SFTP,
      name: 'SFTP',
      validator: IsValidSFTPAddress,
      validationMessage: 'Enter a valid SFTP address',
    },
  ];

  public static ChannelHasUsernamePassword = (channelTypeId: number) =>
    [LegacyNotificationChannelTypeId.FTP, LegacyNotificationChannelTypeId.SFTP].includes(channelTypeId);

  public state: IState = {
    notification: new LegacyNotificationChannel(),
    saving: false,
    deleteNotification: false,
    errors: {},
    showPassword: false,
  };

  get notificationId() {
    return this.props.match.params.notificationId;
  }

  public async componentDidMount() {
    if (this.notificationId) {
      this.setState({ notification: await this.getNotificationChannel() });
    }
  }

  public getNotificationChannel = () => {
    return LegacyNotificationManagementService.GetNotificationChannelById(this.notificationId);
  };

  public deleteNotificationChannel = async () => {
    try {
      this.setState({ saving: true, deleteNotification: false });

      const httpCallConfig = new HttpCallConfig();
      httpCallConfig.successMessage = 'Notification Channel deleted';
      await LegacyNotificationManagementService.DeleteNotificationChannel(
        this.notificationId,
        httpCallConfig
      );

      this.props.history.push('/organization/notifications');
    } catch {
      this.setState({ saving: false });
    }
  };

  public saveNotificationChannel = async () => {
    try {
      if (!this.validateState()) {
        return;
      }

      this.setState({ saving: true });

      if (this.notificationId) {
        const httpCallConfig = new HttpCallConfig();
        httpCallConfig.successMessage = 'Notification updated succesfully';
        await LegacyNotificationManagementService.PutNotificationChannel(
          this.notificationId,
          this.state.notification,
          httpCallConfig
        );
      } else {
        const httpCallConfig = new HttpCallConfig();
        httpCallConfig.successMessage = 'Notification added succesfully.';
        await LegacyNotificationManagementService.PostNotificationChannel(
          this.state.notification,
          httpCallConfig
        );
      }

      this.props.history.push('/organization/notifications');
    } catch {
      this.setState({ saving: false });
    }
  };

  public validateState = () => {
    const { notification } = this.state;
    const errors: IValidation = {};

    // Validate address based on channel type.
    const channelType = LegacyNotificationDetail.AvailableChannelTypes.find(
      c => c.id === notification.notificationChannelTypeId
    );
    if (channelType) {
      if (!channelType.validator(notification.channel.address)) {
        errors['notification.channel.address'] = channelType.validationMessage;
      }
    }

    // Validate username and password.
    if (LegacyNotificationDetail.ChannelHasUsernamePassword(notification.notificationChannelTypeId)) {
      if (!notification.channel.username) {
        errors['notification.channel.username'] = 'Username is required';
      }
      if (!notification.channel.password) {
        errors['notification.channel.password'] = 'Password is required';
      }
    }

    this.setState({ errors });
    return !Object.keys(errors).length;
  };

  public getOnChange = (path: string) => {
    return (event: React.ChangeEvent<any>) => {
      const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
      this.setState(state => this.immutableChange(state, path, value));
    };
  };

  public immutableChange: any = (obj: object, path: string, value: any) => {
    if (!path) {
      return value;
    }
    const relativePaths = path.split('.');
    return {
      ...obj,
      [relativePaths[0]]: this.immutableChange(
        obj[relativePaths[0]],
        relativePaths.slice(1).join('.'),
        value
      ),
    };
  };

  public render() {
    const { classes, history } = this.props;
    const { notification, saving, deleteNotification, errors, showPassword } = this.state;

    return (
      <Paper className={classes.paper}>
        <Grid container={true}>
          <Grid item={true} style={{ flex: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="notification.isDefault"
                  checked={notification.isDefault}
                  onChange={this.getOnChange('notification.isDefault')}
                />
              }
              label="Default"
            />
          </Grid>
          {this.notificationId && (
            <Grid item={true}>
              <Button
                size="small"
                onClick={() => this.setState({ deleteNotification: true })}
                disabled={saving}
              >
                Remove Notification Channel
              </Button>
            </Grid>
          )}
        </Grid>
        <Grid container={true}>
          <Grid item={true}>
            <FormControlLabel
              control={
                <Checkbox
                  name="notification.isEnabled"
                  checked={notification.isEnabled}
                  onChange={this.getOnChange('notification.isEnabled')}
                />
              }
              label="Enabled"
            />
          </Grid>
        </Grid>
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <FormControl>
              <Select
                name="notification.notificationChannelTypeId"
                value={notification.notificationChannelTypeId}
                onChange={(event: React.ChangeEvent<any>) => {
                  this.setState(state =>
                    this.immutableChange(
                      state,
                      'notification.notificationChannelTypeId',
                      event.target.value
                    )
                  );

                  if (!LegacyNotificationDetail.ChannelHasUsernamePassword(event.target.value)) {
                    this.setState(state =>
                      this.immutableChange(state, 'notification.channel.username', null)
                    );
                    this.setState(state =>
                      this.immutableChange(state, 'notification.channel.password', null)
                    );
                  }
                }}
              >
                {LegacyNotificationDetail.AvailableChannelTypes.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <TextField
              name="notification.channel.address"
              label="Address"
              value={notification.channel.address}
              margin="normal"
              required={true}
              onChange={this.getOnChange('notification.channel.address')}
              error={!!errors['notification.channel.address']}
              helperText={errors['notification.channel.address']}
            />
          </Grid>
        </Grid>
        {LegacyNotificationDetail.ChannelHasUsernamePassword(notification.notificationChannelTypeId) ? (
          <Grid container={true} spacing={24}>
            <Grid item={true}>
              <TextField
                name="notification.channel.username"
                label="Username"
                value={notification.channel.username}
                required={true}
                onChange={this.getOnChange('notification.channel.username')}
                error={!!errors['notification.channel.username']}
                helperText={errors['notification.channel.username']}
              />
            </Grid>
            <Grid item={true}>
              <TextField
                name="notification.channel.password"
                label="Password"
                value={notification.channel.password}
                required={true}
                onChange={this.getOnChange('notification.channel.password')}
                error={!!errors['notification.channel.password']}
                helperText={errors['notification.channel.password']}
                type={showPassword ? 'text' : 'password'}
                inputProps={{ autocomplete: 'new-password' }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Toggle password visibility"
                        onClick={() => {
                          this.setState({ showPassword: !showPassword });
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        ) : null}
        <Grid container={true} spacing={24}>
          <Grid item={true}>
            <Button
              variant="outlined"
              color="secondary"
              className={classes.button}
              onClick={() => history.push('/organization/notifications')}
            >
              BACK
            </Button>
          </Grid>
          <Grid item={true}>
            <Button
              variant="contained"
              color="secondary"
              className={classes.button}
              onClick={this.saveNotificationChannel}
              disabled={saving}
            >
              SAVE
            </Button>
          </Grid>
        </Grid>
        <AreYouSureModal
          onClose={() => this.setState({ deleteNotification: false })}
          onConfirm={this.deleteNotificationChannel}
          message="Are you sure you wish to remove this notification channel?"
          open={deleteNotification}
        />
      </Paper>
    );
  }
}

export default withRouter(withStyles(styles)(LegacyNotificationDetail));



// WEBPACK FOOTER //
// ./src/modules/organization/components/LegacyNotificationDetail.tsx